import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationPatterns } from '../../helpers/validation-patterns';
import { CustomValidators } from '../../helpers/custom-validators';
import { catchError, filter, finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { LanguageService } from '../language.service';
import { FormsErrorHandlerService } from '../forms-error-handler.service';
import { SsApiService } from '../api/ss-api.service';
import { CacheService } from '../cache/cache.service';
import { UserService } from './user.service';
import { UserInfoService } from './user-info.service';
import { ContentUpdaterService } from '../content-updater/content-updater.service';
import { ToastMessageService } from '../../shared/modules/toast-message/toast-message.service';
import { EnvironmentService } from '../environment.service';
import { Router } from '@angular/router';
import { BannerType } from '../../../../environments/environment';
import { BannersService } from '../banners.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CookieService } from 'ngx-unificator/services';
import { PaymentMode, UserPaymentModeService } from './user-payment-mode.service';
import { CommonDataService } from '../common-data.service';
import { WrSocketsService } from '../wr-sockets.service';
import { TwoFactorService } from '../two-factor.service';
import { LocalstorageService } from '../localstorage.service';
import { StaticContentService } from '../static-content.service';
import { LocalHistoryService } from '../local-history.service';
import { DEP_AMOUNT_STR } from '../lottery.service';
import { ZendeskChatService } from '../zendesk/zendesk-chat.service';
import { UserProfileService } from './user-profile.service';
import { UserSubscriptionsService } from './user-subscriptions.service';
import { TelegramAppService } from '../telegram-app.service';
import { PTAG_KEY, STAG_URL_KEY, StagDetectorService } from '../stag-detector.service';
import { UbidexService } from '../ubidex.service';
import { LOCAL_STORAGE_FRAUD_KEY_CASHOUT, LOCAL_STORAGE_FRAUD_KEY_DEPOSIT } from '../fraud-check.service';

export interface Banner {
  Title: string;
  LOCAL_STORAGE_STAG_URL_KEY;
  Description: string;
  Image: string;
  SubTitle?: string;
  Prize?: string;
}

export const SPECIFIC_STAGS = [
  '109577', '127787', '103256', '86637', '119585', '133387', '86623',
  '88607', '122462', '126159', '107491', '130063', '105646', '133840',
  '104103', '114605', '84062', '129698', '108592', '129504', '85928',
  '120729', '129499', '129500', '115239', '114243', '84037', '134106',
  '133798', '134106', '85928', '126997', '133840',
];

/**
 * Countries to set EUR currency by default
 */
const eurCountries = [
  '',
];

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  lang = inject(LanguageService);
  private _userProfile = inject(UserProfileService);
  private _fb = inject(FormBuilder);
  private _formErrors = inject(FormsErrorHandlerService);
  private _ssApi = inject(SsApiService);
  private _cache = inject(CacheService);
  private _user = inject(UserService);
  private _userInfo = inject(UserInfoService);
  private _contentUpdater = inject(ContentUpdaterService);
  private _toastMessage = inject(ToastMessageService);
  private _env = inject(EnvironmentService);
  private _router = inject(Router);
  private _banners = inject(BannersService);
  private _cookie = inject(CookieService);
  private _paymentMode = inject(UserPaymentModeService);
  private _commonData = inject(CommonDataService);
  private _twoFactor = inject(TwoFactorService);
  private _storage = inject(LocalstorageService);
  private _static = inject(StaticContentService);
  private _local = inject(LocalHistoryService);
  private _zendesk = inject(ZendeskChatService);
  private _subscriptions = inject(UserSubscriptionsService);
  private _telegramApp = inject(TelegramAppService);
  private _stag = inject(StagDetectorService);
  private _ubidex = inject(UbidexService);


  public isSecondStep: boolean;

  private _registerForm: FormGroup = this._fb.group({
    email: [null, [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: [null, [
      Validators.required,
      Validators.maxLength(128),
      Validators.pattern(ValidationPatterns.noSpaces),
      CustomValidators.changeKey('password_length', Validators.pattern(ValidationPatterns.password)),
    ]],
    nickname: [null, [Validators.required]],
    terms_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    age_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    receive_promos: [false],
    currency: [null],
    country: [null],
    bonus_code: [null],
  });

  private _enhancedRegisterForm: FormGroup = this._fb.group({
    nickname: [null, [Validators.required]],
    email: [null, [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: [null, [
      Validators.required,
      Validators.maxLength(128),
      Validators.pattern(ValidationPatterns.noSpaces),
      CustomValidators.changeKey('password_length', Validators.pattern(ValidationPatterns.password)),
    ]],
    currency: [null, [Validators.required]],
    terms_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    age_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    receive_promos: [false],
    bonus_code: [null],
  });

  private _loginForm: FormGroup = this._fb.group({
    email: [null, [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: [null, [
      Validators.required,
      Validators.maxLength(128),
      CustomValidators.changeKey('password_length', Validators.pattern(ValidationPatterns.password)),
    ]],
    otp_attempt: [null],
  });

  private _loading = false;

  private _bannerForm$: ReplaySubject<Banner> = this.getBanner$('register-form');
  private _bannerLeaveReg$: ReplaySubject<Banner> = this.getBanner$('register-leave');
  private _wrSocket: WrSocketsService = inject(WrSocketsService);

  public partners$: Observable<any> = this._getPartners$();

  public enhancedReg$: Observable<any> = this._static.item({ slug: 'full-register-stags' }).pipe(
    filter(data => data?.length),
    tap(() => {
      this.setDefaultCurrency(this.enhancedRegisterForm);
    }),
  );

  constructor() {
    this._subscriptions.receiveEmailPromosValue$.subscribe(() => this._registerForm.get('receive_promos').setValue(true));
  }

  get loginForm(): FormGroup {
    return this._loginForm;
  }

  get registerForm(): FormGroup {
    return this._registerForm;
  }

  get enhancedRegisterForm(): FormGroup {
    return this._enhancedRegisterForm;
  }

  get loading(): boolean {
    return this._loading;
  }

  get bannerForm$(): ReplaySubject<Banner> {
    return this._bannerForm$;
  }

  get bannerLeaveReg$(): ReplaySubject<Banner> {
    return this._bannerLeaveReg$;
  }

  /**
   * Login form submit handler
   */
  onLoginSubmit() {

    if (this._loginForm.invalid) {
      this._formErrors.applyFormErrors(this._loginForm, null, true);
      return;
    }

    this._loading = true;

    this._ssApi.usersSignIn({
      user: this._twoFactor.twoFactorEnabled ?
        { otp_attempt: this._loginForm.get('otp_attempt').value } :
        this._loginForm.value,
    }).pipe(
      tap((response) => {
        this._loginForm.reset();
        this._cache.clear();
        this._loginForm.markAsUntouched();
        this._user.applyUserInfo(response);
        this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);
        this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_CASHOUT);
      }),
      catchError(error => {
        if (error.status === 422 && error.error.required &&
          error.error.required.includes('one_time_password')) {
          this._loading = false;
          this._twoFactor.twoFactorEnabled$.next(true);
          return EMPTY;
        } else {
          this._toastMessage.error(
            this._formErrors.ssBackendErrorsToArray(error.error)[0] ||
            't.invalid-email-password');
          this._changesFieldErrorsIfSSError(error.error);
          return EMPTY;
        }
      }),
      tap((error) => {
        if (!error?.error?.errors) {
          this._toastMessage.success('t.auth-success');
        }
      }),
      switchMap(() => this._env.env$),
      first(),
      tap(({ data }) => {
        this._proceedRedirects();

        if (this._storage.get(DEP_AMOUNT_STR) &&
          data?.currency?.short !== this._user.info?.currency) {
          this._storage.clearItem(DEP_AMOUNT_STR);
        }
      }),
      switchMap(() => combineLatest([
        this._user.updateAllAccounts(),
        this._user.getPlayerStats(),
      ])),
      switchMap(() => this._stag.resolveStagParam$()),
      tap(() => {
        this._contentUpdater.updateAll();
        this._wrSocket.sendEventLogin();
        this._ubidex.onLogin();
      }),
      finalize(() => this._loading = false),
    ).subscribe();
  }

  onEnhancedRegSubmit(missedFieldsForm: FormGroup) {

    missedFieldsForm.removeControl('receive_promos');
    missedFieldsForm.removeControl('receive_sms_promos');

    if (missedFieldsForm.invalid) {
      this._formErrors.applyFormErrors(missedFieldsForm, null, true);
      return;
    }

    this._userProfile.loading = true;

    const formData = {
      ...missedFieldsForm.getRawValue(),
    };

    this._onSubmitReg$(this.enhancedRegisterForm, formData).pipe(
      switchMap(() => combineLatest([this._user.getUserInfo(), this._user.getPlayerStats()])),
      finalize(() => this._userProfile.loading = false),
    ).subscribe();
  }

  onRegSubmit() {
    this._onSubmitReg$(this.registerForm)
      .pipe(
        switchMap(() => combineLatest([this._user.getUserInfo(), this._user.getPlayerStats()])),
        tap(() => this._telegramApp.completeMissionReducer()),
      )
      .subscribe();
  }

  public getBanner$(alias: string) {
    return this._paymentMode.currentMode$.pipe(
      untilDestroyed(this),
      switchMap((mode) => this._banners.list({
        type: mode === PaymentMode.CRYPTO ? BannerType.REGISTER_BANNER : BannerType.REGISTER_BANNER_FIAT,
      })),
      filter(list => !!list),
      map((list: any[]) => list.find(e => e.alias === alias)),
      shareReplay(1),
    ) as ReplaySubject<any>;
  }

  public onClickStep() {
    const controlsToCheck = ['email', 'password'];
    let isFormValid = true;

    controlsToCheck.forEach(controlName => {
      const control = this.enhancedRegisterForm.get(controlName);
      if (control && control.invalid) {
        control.markAsTouched();
        isFormValid = false;
      }
    });
    if (!isFormValid) {
      return;
    }
    this.isSecondStep = true;
  }

  private _onSubmitReg$(form: FormGroup, enhancedFormValues?: any): Observable<any> {
    this._setAdditionalFields(form);

    if (form.invalid) {
      this._formErrors.applyFormErrors(form, null, true);
      return EMPTY;
    }

    this._loading = true;

    const bonusCode = form.getRawValue().bonus_code;

    return this._ssApi.usersSignUp({
      user: {
        ...form.getRawValue(),
        bonus_code: bonusCode ? bonusCode.toUpperCase() : bonusCode,
      },
    }).pipe(
      tap((response) => {
        this._cache.clear();
        this._user.applyUserInfo(response);
        this._user.updateAllAccounts().subscribe();
        this._contentUpdater.updateAll();
      }),
      catchError(error => {
        this._toastMessage.error(this._formErrors.ssBackendErrorsToArray(error.error, this.isSecondStep)[0]);
        this._formErrors.applyFormErrors(form, {
          errors: {
            ...error.error.errors || {},
            ...error.error.errors && error.error.errors.profile || {},
          },
        }, true);
        this.isSecondStep = false;
        return EMPTY;
      }),
      tap((error) => {
        if (!error?.error?.errors) {
          this._toastMessage.success('t.register-success');
        }
      }),
      switchMap(() => this._userInfo.updatePlayerInfo(enhancedFormValues ? { ...enhancedFormValues } : {
        nickname: this._registerForm.value.nickname,
        country: this._env.env?.country?.short?.toUpperCase(),
      }, 'edition')),
      switchMap(() => this.partners$),
      tap((partners) => {
        this._proceedRedirects(partners);
      }),
      switchMap(() => this._env.env$),
      tap(({ data }) => {
        if (this._storage.get(DEP_AMOUNT_STR) && data?.currency?.short !== this._user.info?.currency) {
          this._storage.clearItem(DEP_AMOUNT_STR);
        }
      }),
      first(),
      finalize(() => {
        this._loading = false;
        this._ubidex.onRegister();
        if (this._zendesk.isOpen) {
          this._zendesk?.hide();
        }
      }),
    );
  }

  public defaultActions(form: FormGroup) {
    this.setDefaultCurrency(form);
    this.setCountry(form);
    this.generateNick(form);
    this._isAgeAcceptanceFieldCheckedForGeo(form);
  }

  /**
   * Check if age acceptance field should be checked
   *
   * @private
   */
  private _isAgeAcceptanceFieldCheckedForGeo(form: FormGroup) {
    this._static.item({
      slug: 'active-age-acceptance-checkbox-by-geo',
    }).pipe(
      filter(item => item?.length),
      tap(() => {
        form.get('age_acceptance')?.setValue(true);
        form.get('terms_acceptance')?.setValue(true);
      }),
    ).subscribe();
  }

  /**
   * Set values to fields that not exists in form
   *
   * @private
   */
  private _setAdditionalFields(form: FormGroup) {
    form.get('age_acceptance')?.setValue(form.get('terms_acceptance')?.value);
    form.get('receive_promos')?.setValue(form.get('receive_promos')?.value);
  }

  /**
   * Set currency by specific stags
   * @private
   */
  private _setCurrencyByStag(form: FormGroup) {
    if (this._paymentMode.isModeCrypto) {
      form.get('currency')?.setValue('BTC');
    } else {
      if (this._env.env.country.short === 'au') {
        this._paymentMode.changePaymentMode(PaymentMode.FIAT);
        form.get('currency')?.setValue('AUD');
      } else if (this._env.env.country.short === 'nz') {
        this._paymentMode.changePaymentMode(PaymentMode.FIAT);
        form.get('currency')?.setValue('NZD');
      } else if (this._env.env.country.short === 'br') {
        this._paymentMode.changePaymentMode(PaymentMode.FIAT);
        form.get('currency')?.setValue('BRL');
      } else {
        form.get('currency').setValue(this._env.env.currency.short || 'USD');
      }
    }
  }

  /**
   * Set currency by default according payment mode
   * @private
   */
  private _setCurrencyByDefault(form: FormGroup) {
    if (this._paymentMode.currentMode === PaymentMode.CRYPTO) {
      form.get('currency').setValue('BTC');
    } else if (this._paymentMode.currentMode === PaymentMode.FIAT) {
      if (eurCountries.includes(this._env.env.country.short)) {
        form.get('currency').setValue('EUR');
      } else {
        form.get('currency').setValue(this._env.env.currency.short || 'USD');
      }
    }
  }

  private _getPartners$(): Observable<string[]> {
    return this._static.item({ slug: 'special-partners' }).pipe(
      filter(list => !!list),
      first(),
      map(list => list[0]),
      switchMap(({ Content }) => {
        const strippedString = Content.replace(/<[^>]*>/g, '').replace(/\n/g, '');
        return of(strippedString.split(','));
      }),
    );
  }

  public setDefaultCurrency(form?: FormGroup) {
    if (SPECIFIC_STAGS.includes(this._cookie.get('id_id'))) {
      this._setCurrencyByStag(form);
    } else {
      this._setCurrencyByDefault(form);
    }
  }

  public generateNick(form: FormGroup) {
    form.get('nickname').setValue(this.generateNickname(10));
  }

  /**
   * Generate random nickname
   * @private
   */
  public generateNickname(length) {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  /**
   * Set country to register form
   */
  public setCountry(form: FormGroup) {
    this._commonData.loaded$.pipe(
      first(),
      tap(() => {
        if (this._commonData.countryList.some(country => country.short === this._env.env.country.short)) {
          form.get('country')?.setValue(this._env.env?.country?.short?.toUpperCase());
          form.get('country')?.disable();
        }
      }),
    ).subscribe();
  }

  private async _proceedRedirects(partners?: any) {

    if (Boolean(this._storage.get(STAG_URL_KEY)) && this._storage.get(STAG_URL_KEY).length &&
      partners?.includes(this._storage.get(PTAG_KEY))) {
      await this._partnersRedirect();
      return;
    }

    if (Array.from(this._local.routes.keys()).some(key => key.includes('promo'))) {
      await this._promoRedirects();
    } else {
      await this._globalRedirects();
    }
  }

  private async _promoRedirects() {
    if (this._local.getUrl('promo-hunt')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-hunt').url);
    }
    if (this._local.getUrl('promo-deposit')) {
      await this._router.navigate(['account', 'deposit'], {
        queryParams: {
          bonusCode: this._local.getUrl('promo-deposit')?.data?.bonusCode,
          successUrl: this._local.getUrl('promo-deposit')?.data?.successUrl,
        },
      });
    }
    if (this._local.getUrl('promo-auth')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-auth').url);
    }
    if (this._local.getUrl('promo-tournaments')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-tournaments').url);
    }
  }

  private async _globalRedirects() {

    const currentCurrencyAmount = this._user.currentCurrency?.amount;
    const currentCurrencyToEquivalent = this._user.currentCurrencyToEquivalent;

    const lotteryUrl = this._local.getUrl('lottery');
    const tournamentsUrl = this._local.getUrl('tournaments');
    const featuresUrl = this._local.getUrl('top-features');

    if (currentCurrencyAmount && currentCurrencyAmount <= currentCurrencyToEquivalent) {
      await this._router.navigate(['/account/deposit']);
    } else if (lotteryUrl) {
      await this._router.navigate(['/account/deposit']);
    } else if (featuresUrl) {
      await this._router.navigate(['/account/deposit']);
    } else if (tournamentsUrl) {
      await this._router.navigateByUrl(tournamentsUrl.url);
    } else {
      await this._router.navigateByUrl('/account/deposit');
    }
  }


  /**
   * Redirect after registration
   * @private
   */
  private async _partnersRedirect() {
    await this._router.navigateByUrl(this._storage.get(STAG_URL_KEY));
    this._storage.clearItem(STAG_URL_KEY);
  }

  /**
   * If error from SS reset errors for some fields if valueChanges
   * @private
   */
  private _changesFieldErrorsIfSSError(errors) {
    const controls = Object.keys(errors.errors);

    if (controls.length) {
      this.loginForm.valueChanges.pipe(
        tap(() => {
          controls.forEach(control => this.loginForm.get(control).setErrors(null));
          this._formErrors.applyFormErrors(this.loginForm, null, true);
        }),
      ).subscribe();
    }
  }
}
